import { useState } from 'react';
import { 
  Users, 
  MoreHorizontal, 
  Shield, 
  Trash2, 
  UserPlus, 
  Search,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Clock,
  X,
  User
} from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
import { useUI } from '../../context/UIContext';
import { useMembers, useUpdateMemberRole, useRemoveMember } from '@/api/queries';
import LoadingState from '../ui/LoadingState';
import ErrorState from '../ui/ErrorState';
import EmptyState from '../ui/EmptyState';
import ModalContainer from '../layout/ModalContainer';
import { Vision360 } from '../common/Vision360';

interface MembrosTabProps {
  workspaceId?: string;
}

export default function MembrosTab({ workspaceId }: MembrosTabProps) {
  const { setGlobalFilters } = useNavigation();
  const { setCurrentView } = useUI();
  const [search, setSearch] = useState('');
  const [selectedMemberFor360, setSelectedMemberFor360] = useState<any>(null);
  const [activeModalTab, setActiveModalTab] = useState<'dados' | 'visao_360'>('dados');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newInvite, setNewInvite] = useState({ email: '', role: 'MEMBER' });
  const [sendingInvite, setSendingInvite] = useState(false);

  const { data: members = [], isLoading, error, refetch } = useMembers(workspaceId || '');
  const updateRoleMutation = useUpdateMemberRole();
  const removeMemberMutation = useRemoveMember();

  const filteredMembers = (Array.isArray(members) ? members : []).filter((m: any) => {
    const userName = m.User?.fullName || m.User?.preferredName || '';
    const userEmail = m.User?.email || '';
    const roleName = m.OrganizationRole?.name || '';
    return userName.toLowerCase().includes(search.toLowerCase()) || 
           userEmail.toLowerCase().includes(search.toLowerCase()) ||
           roleName.toLowerCase().includes(search.toLowerCase());
  });

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    try {
      await updateRoleMutation.mutateAsync({ memberId, roleKey: newRole, workspaceId: workspaceId || '' });
      await refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Tem certeza que deseja remover este membro?')) return;
    try {
      await removeMemberMutation.mutateAsync({ memberId, workspaceId: workspaceId || '' });
      await refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInvite.email || !workspaceId) return;
    try {
      setSendingInvite(true);
      // Usar o hook de invitations se disponível, ou redirecionar para a tab de convites
      alert('Use a aba Convites para enviar convites.');
      setShowInviteModal(false);
      setNewInvite({ email: '', role: 'MEMBER' });
    } catch (err) {
      console.error(err);
      alert('Erro ao enviar o convite.');
    } finally {
      setSendingInvite(false);
    }
  };

  if (isLoading) return <LoadingState message="Carregando membros..." />;
  if (error) return <ErrorState message={error.message || 'Erro ao carregar membros'} retry={() => refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-[#0F172A0A] rounded-[24px] p-4 shadow-[0_4px_20px_rgb(0,0,0,0.01)]">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
          <input 
            type="text" 
            placeholder="Buscar por nome, e-mail ou cargo..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#FAFAFB] border-none rounded-xl py-2.5 pl-11 pr-4 text-sm font-medium focus:ring-2 focus:ring-black/5 transition-all outline-none text-[#111111]"
          />
        </div>
        <button 
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 bg-black text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-neutral-800 transition-all shadow-sm"
        >
          <UserPlus size={16} /> Convidar Membro
        </button>
      </div>

      <div className="bg-white border border-[#0F172A0A] rounded-[32px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FAFAFB] border-b border-[#0F172A0A]">
                <th className="px-8 py-5 text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Membro</th>
                <th className="px-8 py-5 text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Cargo / Função</th>
                <th className="px-8 py-5 text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Papel</th>
                <th className="px-8 py-5 text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Status</th>
                <th className="px-8 py-5 text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Acesso</th>
                <th className="px-8 py-5 text-[11px] font-bold text-[#64748B] uppercase tracking-widest text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0F172A08]">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-8 py-12 text-center text-sm text-[#64748B] font-medium">
                    <EmptyState title="Nenhum membro encontrado" description="Adicione membros para começar." />
                  </td>
                </tr>
              ) : filteredMembers.map((member: any) => (
                <tr 
                  key={member.id} 
                  className="hover:bg-[#FAFAFB]/50 transition-colors group cursor-pointer"
                  onClick={() => {
                    setGlobalFilters(prev => ({ ...prev, memberId: member.id }));
                    setCurrentView('membro-workspace');
                  }}
                >
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#FAFAFB] border border-[#0F172A0A] overflow-hidden flex items-center justify-center text-[#111111] font-bold text-sm shadow-sm flex-shrink-0">
                        {member.User?.avatarUrl ? (
                          <img src={member.User.avatarUrl} alt={member.User.fullName || member.User.preferredName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          (member.User?.fullName || member.User?.preferredName || 'U').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-bold text-[#111111] group-hover:text-indigo-600 transition-colors truncate">{member.User?.fullName || member.User?.preferredName || 'Membro'}</span>
                        <span className="text-xs text-[#64748B] font-medium truncate">{member.User?.email || 'sem email'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className="text-sm font-bold text-[#111111]">{member.OrganizationRole?.name || 'Colaborador'}</span>
                  </td>
                  <td className="px-8 py-5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <Shield size={14} className={member.OrganizationRole?.key === 'OWNER' ? 'text-indigo-500' : member.OrganizationRole?.key === 'ADMIN' ? 'text-amber-500' : 'text-[#94A3B8]'} />
                      <select 
                        value={member.OrganizationRole?.key || 'MEMBER'}
                        onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                        disabled={member.OrganizationRole?.key === 'OWNER'}
                        className="bg-transparent border-none text-sm font-bold text-[#111111] focus:ring-0 cursor-pointer p-0 pr-8"
                      >
                        <option value="OWNER">Proprietário</option>
                        <option value="ADMIN">Administrador</option>
                        <option value="MANAGER">Gerente</option>
                        <option value="MEMBER">Membro</option>
                        <option value="VIEWER">Visualizador</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      member.isActive !== false ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                    }`}>
                      {member.isActive !== false ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      {member.isActive !== false ? 'Ativo' : 'Inativo'}
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2 text-xs font-medium text-[#64748B]">
                      <Clock size={14} className="text-[#94A3B8]" />
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-8 py-5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={member.OrganizationRole?.key === 'OWNER'}
                        className="p-2 rounded-lg text-[#64748B] hover:text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Remover Membro"
                      >
                        <Trash2 size={16} />
                      </button>
                      <button 
                        onClick={() => setSelectedMemberFor360(member)}
                        className="p-2 rounded-lg text-[#64748B] hover:text-[#111111] hover:bg-[#F1F5F9] transition-all"
                        title="Ver Perfil"
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedMemberFor360 && (
        <ModalContainer 
          isOpen={!!selectedMemberFor360} 
          onClose={() => setSelectedMemberFor360(null)} 
          maxWidth={activeModalTab === 'visao_360' ? 'max-w-4xl' : 'max-w-xl'}
        >
          <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between bg-[#FAFAFA]/50">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-full bg-slate-100 border border-[#0F172A0A] overflow-hidden flex items-center justify-center text-[#111111] font-bold text-sm shadow-sm flex-shrink-0">
                {selectedMemberFor360.User?.avatarUrl ? (
                  <img src={selectedMemberFor360.User.avatarUrl} alt={selectedMemberFor360.User.fullName || selectedMemberFor360.User.preferredName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  (selectedMemberFor360.User?.fullName || selectedMemberFor360.User?.preferredName || 'U').charAt(0).toUpperCase()
                )}
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#111111]">{selectedMemberFor360.User?.fullName || selectedMemberFor360.User?.preferredName || 'Membro'}</h3>
                <p className="text-[10px] font-medium text-[#64748B] mt-0.5">{selectedMemberFor360.User?.email}</p>
              </div>
            </div>
            <button 
              onClick={() => setSelectedMemberFor360(null)}
              className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
            >
              <X size={15} strokeWidth={2.5} />
            </button>
          </div>

          <div className="flex px-6 border-b border-[#0F172A0F] bg-[#FAFAFA]/50 gap-6">
            <button 
              onClick={() => setActiveModalTab('dados')}
              className={`py-3 px-1 border-b-2 text-xs font-bold transition-all ${
                activeModalTab === 'dados' ? 'border-[#111111] text-[#111111]' : 'border-transparent text-[#64748B] hover:text-[#111111]'
              }`}
            >
              Perfil & Atribuições
            </button>
            <button 
              onClick={() => setActiveModalTab('visao_360')}
              className={`py-3 px-1 border-b-2 text-xs font-bold transition-all ${
                activeModalTab === 'visao_360' ? 'border-[#111111] text-[#111111]' : 'border-transparent text-[#64748B] hover:text-[#111111]'
              }`}
            >
              Visão 360°
            </button>
          </div>

          {activeModalTab === 'visao_360' ? (
            <div className="h-[60vh] overflow-y-auto">
              <Vision360 entityType="team" entityId={selectedMemberFor360.id} entityName={selectedMemberFor360.User?.fullName || selectedMemberFor360.User?.preferredName} entityData={selectedMemberFor360} />
            </div>
          ) : (
            <div className="p-6 flex flex-col gap-6 text-left bg-white">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Cargo / Função</label>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{selectedMemberFor360.OrganizationRole?.name || 'Colaborador'}</p>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Status na Plataforma</label>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{selectedMemberFor360.isActive !== false ? 'Ativo' : 'Inativo'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Papel de Acesso</label>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{selectedMemberFor360.OrganizationRole?.key || 'MEMBER'}</p>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Data de Ingresso</label>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{new Date(selectedMemberFor360.joinedAt).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-[#0F172A05] flex justify-end">
                <button 
                  onClick={() => setSelectedMemberFor360(null)}
                  className="px-5 py-2 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-xl transition-all"
                >
                  Fechar Perfil
                </button>
              </div>
            </div>
          )}
        </ModalContainer>
      )}

      {showInviteModal && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowInviteModal(false)}
        >
          <div 
            className="bg-white rounded-[32px] w-full max-w-md overflow-hidden shadow-[0_20px_70px_rgba(0,0,0,0.15)] animate-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 pb-0 flex items-center justify-between">
              <div className="w-12 h-12 rounded-2xl bg-black flex items-center justify-center text-white shadow-lg">
                <UserPlus size={24} />
              </div>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors text-[#64748B]"
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>

            <form onSubmit={handleSendInvite} className="p-8 pt-6 flex flex-col gap-6">
              <div>
                <h3 className="text-xl font-bold text-[#111111] tracking-tight">Convidar para a Equipe</h3>
                <p className="text-sm text-[#64748B] font-medium">Use a aba Convites para gerenciar convites</p>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest px-1">E-mail do Convidado</label>
                  <input 
                    type="email" 
                    required
                    placeholder="exemplo@email.com"
                    value={newInvite.email}
                    onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                    className="w-full bg-[#FAFAFB] border border-[#0F172A0A] rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-black/5 outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest px-1">Papel / Permissões</label>
                  <select 
                    value={newInvite.role}
                    onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value })}
                    className="w-full bg-[#FAFAFB] border border-[#0F172A0A] rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-black/5 outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="MEMBER">Membro Padrão</option>
                    <option value="ADMIN">Administrador</option>
                    <option value="MANAGER">Gerente de Projeto</option>
                    <option value="VIEWER">Apenas Leitura</option>
                  </select>
                </div>
              </div>

              <button 
                type="submit"
                disabled={sendingInvite}
                className="w-full py-4 rounded-2xl bg-black text-white text-sm font-bold hover:bg-neutral-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-black/10"
              >
                {sendingInvite ? 'Enviando...' : 'Enviar Convite'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
